import React from 'react'
import { useState, useEffect } from 'react';
import { PageObj } from '../Interfaces/TestCaseInterface';
import { PageSelector } from './PageSelector';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import './PageList.css'
import { InfoTooltip } from './InfoTooltip';

interface PageListProps {
  pages: PageObj[],
  setTestPages: (pid: number, newPage: PageObj) => void,
  setPages: React.Dispatch<React.SetStateAction<PageObj[]>>,
  newPid: number,
  setNewPid: React.Dispatch<React.SetStateAction<number>>,
  activePagePid: number,
  setActivePagePid: React.Dispatch<React.SetStateAction<number>>,
  pageNames: Array<string>,
  journeyNames: Record<string, Array<string>>,
  selectUserJourney: (arg0: string) => void,
  validTestBookends: boolean,
  pageTemplates: Record<string, Array<string>>,
  editable: boolean,
  saveCurrentPageBeforePageChange: (newPageId: number)=> void
}

export const PageList = (props: PageListProps) => {
  const [selectablePages, setSelectablePages] = useState<Array<string>>([]);
  const [showJourneySelection, setShowJourneySelection] = useState(false);
  const [viewingJourney, setViewingJourney] = useState<string>('');
  const [chosenJourney, setChosenJourney] = useState<string>('');

  useEffect(() => {
    let validPages = props.pages.map(page => page.name);

    setSelectablePages(validPages);
  }, [props.pages, props.pageNames])

  useEffect(() => {
    if (chosenJourney !== '') {
      props.selectUserJourney(chosenJourney);
      setChosenJourney('');
      setViewingJourney('');
    }
  }, [chosenJourney])

  const updatePageIx = (pid: number, adjustment: number) => {
    let oldIx: number = props.pages.findIndex((e: PageObj) => e.pid === pid);
    let newPages = [...props.pages];
    if (adjustment + oldIx < 0)
      adjustment = oldIx;
    if (adjustment + oldIx > props.pages.length - 1)
      adjustment = props.pages.length - 1 - oldIx;
    if (adjustment === 0)
      return;
    let oldPage = props.pages[oldIx];
    if (adjustment < 0) {
      for (let i = 0; i > adjustment; i--) {
        newPages[oldIx + i] = newPages[oldIx + i - 1];
      }
      newPages[oldIx + adjustment] = oldPage;
    } else { // adjustment > 0
      for (let i = 0; i < adjustment; i++) {
        newPages[oldIx + i] = newPages[oldIx + i + 1];
      }
      newPages[oldIx + adjustment] = oldPage;
    }
    props.setPages(newPages);
  } // updatePageIx

  const deletePage = (pid: number) => {
    props.setPages(props.pages.filter(e => e.pid !== pid));
    if (props.activePagePid === pid && props.pages.length > 0)
      props.setActivePagePid(-1);
  } // deletePage()

  const addPage = () => {
    let newPage: PageObj = {
      pid: props.newPid,
      name: props.pageNames.length > 0 ? props.pageNames[0] : "",
      inputs: []
    }
    props.setNewPid(props.newPid + 1);
    let newPages = [...props.pages];
    newPages.push(newPage);
    props.setPages(newPages);
  } // addPage()

  return (
    <Box className='mb-3'>
      <Container className="pageList">
        <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
            <Box>
              <Typography variant='h5'>
                Pages
              <InfoTooltip position='right' helpText='List of pages that will be tested by Test Runner.' />
              </Typography>
            </Box>
            <Box sx={{alignContent: 'end'}}>
              {Object.keys(props.journeyNames).length > 0 ? <Button disabled={!props.editable} variant='contained' size='small' className='mb-3' onClick={() => setShowJourneySelection(true)}>Load User Journey</Button> : <></>}
              <Button disabled={!props.editable} className='mb-3 ms-2' variant='outlined' color='secondary' size='small' onClick={e => addPage()}>Add Page</Button>
            </Box>
        </Box>
        <List className='test-case-column'>
          <ListItemButton onClick={e => props.activePagePid !== -1 ? props.saveCurrentPageBeforePageChange(-1) : null}>
            <ListItemText primary='Test Definition' />
            <IconButton disabled edge='end'>
              {props.validTestBookends ?
                <CheckCircleIcon fontSize='small' color='success'/>
                : <WarningIcon color='warning' />}

            </IconButton>
          </ListItemButton>
          <Divider component="li" variant="inset" light />
          {props.pages !== undefined ?
            props.pages.map(page => {
              return (
                <React.Fragment key={`${page.pid}`}>
                  <PageSelector
                    page={page}
                    setTestPages={props.setTestPages}
                    updatePageIx={updatePageIx}
                    deletePage={deletePage}
                    activePagePid={props.activePagePid}
                    setActivePagePid={props.setActivePagePid}
                    pageNames={props.pageNames.filter(pageName => !selectablePages.includes(pageName) || page.name === pageName)}
                    pageTemplates={props.pageTemplates}
                    editable={props.editable}
                    saveCurrentPageBeforePageChange={props.saveCurrentPageBeforePageChange} />
                  <Divider component="li" variant="inset" light />
                </React.Fragment>
            )}) : <></>}
        </List>
      </Container>
      <Dialog open={showJourneySelection} onClose={() => setShowJourneySelection(false)}>
        <DialogTitle>Choose a User Journey</DialogTitle>
        <DialogContent>
          <TextField 
          sx={{marginTop: '1rem'}}
            select
            fullWidth
            label='Select Journey'
            value={viewingJourney}
            onChange={(e) => setViewingJourney(e.target.value)}>
            {Object.keys(props.journeyNames).map((journey) => <MenuItem key={journey} value={journey}>{journey}</MenuItem>)}
          </TextField>
          {
            props.journeyNames[viewingJourney] !== undefined ?
              <List>
                {props.journeyNames[viewingJourney].map((pageName, index) => <ListItem key={`${pageName}-${index}`}><ListItemText>{pageName}</ListItemText></ListItem>)}
              </List> : <></>
          }
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => { setViewingJourney(''); setShowJourneySelection(false); }}>
            Close
          </Button>
          <Button disabled={viewingJourney === ''} variant="contained" onClick={() => { setChosenJourney(viewingJourney); setShowJourneySelection(false) }}>
            Load Journey
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
} // PageList() render fn
